// FILE: src/components/admin/ContentBits.jsx
// Building blocks for the course content pages (WP-5): curriculum status and
// check results as icon + words (never colour alone, MTO 5.4), the form
// error summary that links each problem to its field, and a confirmation
// dialog that shows a refusal in words and stays open.
import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Link,
  Stack,
  Typography
} from "@mui/material";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import { StatusChip } from "../ui";
import IconDialogTitle from "../student/IconDialogTitle";
import { checkStatusLabel, curriculumStatusLabel } from "../../i18n/labels";
import { errorMessage } from "../../i18n/errors";

const CURRICULUM_STATUS = {
  DRAFT: { tone: "info", Icon: EditNoteOutlinedIcon },
  APPROVED: { tone: "success", Icon: VerifiedOutlinedIcon },
  RETIRED: { tone: "default", Icon: Inventory2OutlinedIcon }
};

export function CurriculumStatusChip({ status, ...rest }) {
  const { tone, Icon } = CURRICULUM_STATUS[status] || CURRICULUM_STATUS.DRAFT;
  return <StatusChip tone={tone} icon={<Icon />} label={curriculumStatusLabel(status)} {...rest} />;
}

const CHECK_STATUS = {
  PASS: { tone: "success", Icon: CheckCircleOutlineIcon },
  FAIL: { tone: "error", Icon: HighlightOffIcon },
  NOT_CHECKED: { tone: "default", Icon: HelpOutlineIcon }
};

export function CheckStatusChip({ status, ...rest }) {
  const { tone, Icon } = CHECK_STATUS[status] || CHECK_STATUS.NOT_CHECKED;
  return <StatusChip tone={tone} icon={<Icon />} label={checkStatusLabel(status)} {...rest} />;
}

/**
 * A page's actions under its title (PageHeader children). Used where there
 * are more actions than fit beside the title without squeezing it.
 */
export function ActionBar({ children, label = "Actions" }) {
  return (
    <Stack direction="row" role="group" aria-label={label} sx={{ flexWrap: "wrap", gap: 1.5, mt: 2.5 }}>
      {children}
    </Stack>
  );
}

/** "Practice, not scored" (MTO glossary: practice activities are untracked; matrix 3.7). */
export function PracticeChip(props) {
  return <StatusChip tone="info" icon={<SchoolOutlinedIcon />} label="Practice, not scored" {...props} />;
}

/**
 * The problems of a refused or invalid form, each linked to its field
 * (WCAG 3.3.1). `items`: [{ field, label, message }]; `idFor(field)` gives
 * the input's id. Focus moves here (tabIndex -1) when it appears.
 */
export const ProblemSummary = React.forwardRef(function ProblemSummary({ title, intro, items, idFor }, ref) {
  return (
    <Alert severity="error" ref={ref} tabIndex={-1} role="alert" data-testid="problem-summary">
      <AlertTitle>{title}</AlertTitle>
      {intro && <Typography sx={{ mb: items?.length ? 1 : 0 }}>{intro}</Typography>}
      {items?.length > 0 && (
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {items.map((it) => {
            const id = idFor ? idFor(it.field) : null;
            return (
              <li key={it.field}>
                {id ? (
                  <Link
                    href={`#${id}`}
                    color="inherit"
                    onClick={(e) => {
                      const el = document.getElementById(id);
                      if (el) {
                        e.preventDefault();
                        el.focus();
                      }
                    }}
                  >
                    {it.label}
                  </Link>
                ) : (
                  <strong>{it.label}</strong>
                )}
                : {it.message}
              </li>
            );
          })}
        </Box>
      )}
    </Alert>
  );
});

/**
 * A yes/no dialog for a consequential action (delete, approve, retire).
 * A refusal is shown inside it in words (errorMessage + `errorDetail`) and
 * the dialog stays open; Cancel (or Escape) always closes it.
 */
export function ConfirmDialog({
  open,
  id,
  title,
  icon,
  tone = "warning",
  description,
  children,
  confirmLabel,
  busyLabel = "Working…",
  confirmColor = "primary",
  busy = false,
  error = null,
  errorDetail = null,
  onConfirm,
  onClose
}) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby={`${id}-title`} aria-describedby={`${id}-description`}>
      <IconDialogTitle id={`${id}-title`} icon={icon} tone={tone}>
        {title}
      </IconDialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {description && <DialogContentText id={`${id}-description`}>{description}</DialogContentText>}
          {children}
          {error && (
            <Alert severity="error" role="alert">
              {errorMessage(error)}
              {errorDetail && <Box sx={{ mt: 1 }}>{errorDetail}</Box>}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: "wrap" }}>
        <Button onClick={onClose} variant="outlined" disabled={busy} autoFocus>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" color={confirmColor} disabled={busy}>
          {busy ? busyLabel : confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
